import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { countryData as fallbackCountryData } from "../data/siteData";
import { ArrowRight, Globe } from "lucide-react";
import { getApiUrl, getAssetUrl } from "../apiConfig";

import australiaImg from "../assets/countries/australia.webp";
import dubaiImg from "../assets/countries/dubai.png";
import europeImg from "../assets/countries/europe.webp";
import irelandImg from "../assets/countries/ireland.png";
import newZealandImg from "../assets/countries/new-zealand.png";
import ukImg from "../assets/countries/uk.png";
import usaImg from "../assets/countries/usa.png";
import canadaImg from "../assets/countries/canada.png";
import singaporeImg from "../assets/countries/Singapore.webp";

const DEFAULT_MEMOJIS = {
  "united-kingdom": ukImg,
  "uk": ukImg,
  "europe": europeImg,
  "usa": usaImg,
  "australia": australiaImg,
  "canada": canadaImg,
  "new-zealand": newZealandImg,
  "ireland": irelandImg,
  "dubai": dubaiImg,
  "singapore": singaporeImg,
};

const resolveCountryImage = (img, slug) => {
  if (!img || img === slug || DEFAULT_MEMOJIS[img]) {
    return DEFAULT_MEMOJIS[img] || DEFAULT_MEMOJIS[slug] || null;
  }
  if (typeof img === "string") {
    if (img.startsWith("http://") || img.startsWith("https://") || img.startsWith("data:")) {
      return img;
    }
    return getAssetUrl(img.startsWith("/") ? img : `/${img}`);
  }
  return img;
};

export default function StudyAbroadListingPage() {
  const navigate = useNavigate();
  const [pageData, setPageData] = useState(null);

  useEffect(() => {
    let isMounted = true;
    const fetchPage = async () => {
      try {
        const response = await fetch(getApiUrl("/api/pages/study-abroad"), {
          headers: { "ngrok-skip-browser-warning": "true" },
        });
        const data = await response.json();
        if (isMounted && data.success && data.page) {
          let content = data.page.content;
          while (typeof content === "string") {
            try {
              content = JSON.parse(content);
            } catch (e) {
              break;
            }
          }
          setPageData({ ...data.page, content });
        }
      } catch (err) {
        console.warn("Failed to load /api/pages/study-abroad, using fallbacks:", err);
      }
    };

    fetchPage();
    return () => {
      isMounted = false;
    };
  }, []);

  const hero = pageData?.content?.hero || {};
  const badgeText = hero.badge || "Explore the World";
  const heroTitle = hero.title || "Choose Your Study Destination";
  const heroDescription =
    hero.description ||
    "We've helped thousands of students settle in over 10+ countries. Pick your dream destination and let us handle the admissions, visa preparation, and landing guidelines.";

  // Countries list: DB countries or static fallbacks
  const dbCountries = pageData?.content?.countries;
  const countriesList = Array.isArray(dbCountries) && dbCountries.length > 0
    ? dbCountries.filter((c) => c.isActive !== false)
    : Object.entries(fallbackCountryData).map(([key, country]) => ({
        id: key,
        slug: key,
        name: country.name,
        flag: country.flag,
        image: country.image,
        description: country.description,
        buttonText: "Explore Universities",
      }));

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.08, delayChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] },
    },
  };

  // Render title with gradient for words if appropriate
  const renderTitle = () => {
    if (heroTitle.includes("Study Destination")) {
      const parts = heroTitle.split("Study Destination");
      return (
        <>
          {parts[0]}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-600 via-blue-500 to-teal-500">
            Study Destination
          </span>
          {parts[1] || ""}
        </>
      );
    }
    return heroTitle;
  };

  return (
    <main className="min-h-screen bg-[#F8FAFC] pb-24 text-slate-800 font-sans relative overflow-hidden">
      {/* Background Glows */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[350px] bg-brand-50/70 rounded-full blur-[130px] pointer-events-none" />
      <div className="absolute top-20 right-0 w-[450px] h-[300px] bg-indigo-50/60 rounded-full blur-[120px] pointer-events-none" />

      {/* Header / Hero Section */}
      <section className="relative pt-20 pb-12 px-4 md:px-8 text-center max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-4"
        >
          <span className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.2em] text-brand-600 px-4 py-2 bg-brand-50 rounded-full border border-brand-100 shadow-sm">
            <Globe className="w-3.5 h-3.5 animate-spin" style={{ animationDuration: "12s" }} />
            {badgeText}
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="text-4xl md:text-6xl font-black text-slate-900 tracking-tight leading-tight mb-6"
        >
          {renderTitle()}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-lg md:text-xl text-slate-500 font-medium leading-relaxed"
        >
          {heroDescription}
        </motion.p>
      </section>

      {/* Country Grid */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 relative z-10">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {countriesList.map((country, index) => {
            const countryKey = country.slug || country.id || index;
            const imgSrc = resolveCountryImage(country.image, country.slug);

            return (
              <motion.div
                key={countryKey}
                variants={itemVariants}
                onClick={() => navigate(`/study-abroad/${country.slug || countryKey}`)}
                className="group relative bg-white/70 backdrop-blur-md rounded-[2.5rem] p-8 md:p-10 shadow-[0_10px_40px_-10px_rgba(0,0,0,0.04)] ring-1 ring-slate-100 hover:ring-brand-200/50 hover:bg-white hover:shadow-xl hover:-translate-y-2 transition-all duration-500 flex flex-col justify-between overflow-hidden cursor-pointer"
              >
                {/* Soft background glows on hover */}
                <div className="absolute -right-20 -top-20 h-48 w-48 rounded-full bg-brand-50/35 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />

                <div className="relative z-10">
                  {/* Flag and Heading */}
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-black text-slate-900 tracking-tight flex items-center gap-3">
                      {country.name}
                      {country.flag && (
                        <img
                          src={country.flag}
                          alt={`${country.name} flag`}
                          className="h-5 w-auto object-contain rounded-md filter drop-shadow-sm select-none"
                        />
                      )}
                    </h2>
                  </div>

                  {/* Memoji Logo Container */}
                  <div className="h-40 flex items-center justify-center mb-6 bg-slate-50/50 rounded-3xl p-4 border border-slate-100/50 group-hover:bg-brand-50/30 transition-colors duration-500">
                    {imgSrc ? (
                      <img
                        src={imgSrc}
                        alt={`${country.name} memoji`}
                        className="h-28 w-auto object-contain transition-transform duration-500 group-hover:scale-110"
                        style={{ filter: "drop-shadow(0 8px 16px rgba(0,0,0,0.06))" }}
                      />
                    ) : (
                      <span className="text-4xl">🎓</span>
                    )}
                  </div>

                  {/* Description */}
                  <p className="text-base font-semibold leading-relaxed text-slate-500 mb-8">
                    {country.description}
                  </p>
                </div>

                {/* Action Button */}
                <div className="relative z-10 pt-4 mt-auto border-t border-slate-50">
                  <div className="group/btn inline-flex items-center gap-2.5 text-sm font-black uppercase tracking-widest text-slate-900 transition-colors hover:text-brand-600">
                    {country.buttonText || "Explore Universities"}
                    <ArrowRight className="h-4.5 w-4.5 transition-transform duration-300 group-hover/btn:translate-x-1.5" />
                  </div>
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </section>
    </main>
  );
}
